import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Dialog, Field, Input, Select, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CURRENCIES, refusalMessage, SupplierClient, type SupplierDTO } from "../../api/manufacturing";
import { WarehouseClient } from "../../api/product";
import { ReplenishmentClient, replenishmentKeys, type SupplierLeadTimeReview } from "../../api/replenishment";
import { NexpimView } from "../../views";
import { paymentTermsLabel } from "../purchasing/paymentTerms";
import { supplierErrors } from "./SupplierDetailPage";

/**
 * Suppliers, busiest first.
 *
 * <p>747 rows imported from SIGM, most of which carry a code, a name and
 * nothing else. The list opens sorted by how many orders went to each one, so
 * the dozen the plant actually buys from are on top, with what is still open
 * beside it. A row opens the supplier's page: contacts, every order, the parts
 * it sells, and the fields below, which are edited there.</p>
 *
 * <p>The purchasing terms (payment days, lead time and how it is known, the
 * order cycle and day, the subcontract warehouse) drive replenishment and the
 * cash projection. Blank means "use the purchasing settings' default". Beside
 * the typed lead time sits the one measured from the supplier's own receipts,
 * flagged when the two drift apart.</p>
 */
export function SuppliersPage() {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const allowed = hasView(NexpimView.SUPPLIERS);

  const suppliers = useMemo(() => new SupplierClient(http), [http]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["supplier", "all"],
    queryFn: () => suppliers.findAll(),
    enabled: allowed,
    staleTime: 5 * 60_000,
  });

  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    enabled: allowed,
    staleTime: 30 * 60_000,
  });
  const warehouseCode = useMemo(() => new Map((warehouses.data ?? []).map((w) => [w.id, w.code])), [warehouses.data]);

  // Measured lead times are a review, not part of the supplier row: fetched
  // once and joined here, so the grid stays one resource.
  const leadTimes = useQuery({
    queryKey: replenishmentKeys.reviewLeadTimes,
    queryFn: () => new ReplenishmentClient(http).leadTimeReview(),
    enabled: allowed,
    staleTime: 10 * 60_000,
  });
  const measuredBySupplier = useMemo(
    () => new Map<number, SupplierLeadTimeReview>((leadTimes.data ?? []).map((row) => [row.supplier_id, row])),
    [leadTimes.data],
  );

  const weekdays = useMemo(
    () => [
      t("weekday.1", "Monday"),
      t("weekday.2", "Tuesday"),
      t("weekday.3", "Wednesday"),
      t("weekday.4", "Thursday"),
      t("weekday.5", "Friday"),
      t("weekday.6", "Saturday"),
      t("weekday.7", "Sunday"),
    ],
    [t],
  );

  const supplierCols = useMemo(() => {
    const c = defineColumns<SupplierDTO>();
    return [
      c.text("code", (row) => row.code, { headerKey: "column.supplier.code", priority: 1 }),
      c.text("name", (row) => row.name, { headerKey: "column.supplier.name", priority: 1 }),
      c.number("order_count", (row) => row.order_count ?? 0, {
        header: t("column.supplier.order-count", "Orders"),
        filter: "range",
        priority: 1,
      }),
      c.custom(
        "open_order_count",
        (row) => row.open_order_count ?? 0,
        (row) =>
          row.open_order_count ? (
            <Badge variant="info" size="sm">
              {row.open_order_count}
            </Badge>
          ) : (
            <span />
          ),
        { header: t("column.supplier.open-orders", "Open"), filter: "range", align: "right", priority: 1 },
      ),
      c.text("last_order_date", (row) => row.last_order_date ?? "", {
        header: t("column.supplier.last-order", "Last order"),
        filter: "daterange",
        priority: 2,
      }),
      c.text("legal_name", (row) => row.legal_name ?? "", {
        headerKey: "column.supplier.legal-name",
        defaultVisible: false,
      }),
      c.text("city", (row) => row.city ?? "", { headerKey: "column.city", priority: 3 }),
      c.text("country", (row) => row.country ?? "", { headerKey: "column.country", filter: "select", defaultVisible: false }),
      c.text("currency", (row) => row.currency, { headerKey: "column.currency", filter: "select", priority: 3 }),
      c.text("phone", (row) => row.phone ?? "", { headerKey: "column.phone", priority: 2 }),
      c.text("email_website", (row) => row.website ?? "", {
        headerKey: "column.supplier.website",
        defaultVisible: false,
      }),
      c.text("payment_terms", (row) => paymentTermsLabel(row.payment_terms, row.payment_terms_days, row.deposit_percent, t), {
        headerKey: "column.supplier.payment-terms",
        filter: "select",
        priority: 3,
      }),
      c.custom(
        "lead_time_days",
        (row) => row.lead_time_days,
        (row) => <LeadTimeCell supplier={row} measured={measuredBySupplier.get(row.id)} />,
        { headerKey: "column.supplier.lead-time", filter: "range", priority: 3 },
      ),
      c.custom(
        "lead_time_mode",
        (row) => row.lead_time_mode,
        (row) => (
          <Badge variant={row.lead_time_mode === "MEASURED" ? "info" : "neutral"} size="sm">
            {row.lead_time_mode === "MEASURED" ? t("supplier.lead-mode.measured", "Measured") : t("supplier.lead-mode.fixed", "Fixed")}
          </Badge>
        ),
        {
          headerKey: "column.supplier.lead-mode",
          filter: "select",
          // Labelled, or the filter offered the raw enum values.
          filterOptions: [
            { value: "FIXED", label: t("supplier.lead-mode.fixed", "Fixed") },
            { value: "MEASURED", label: t("supplier.lead-mode.measured", "Measured") },
          ],
          defaultVisible: false,
        },
      ),
      c.number("order_cycle_days", (row) => row.order_cycle_days, {
        headerKey: "column.supplier.order-cycle",
        filter: "range",
        defaultVisible: false,
      }),
      c.text("order_weekday", (row) => (row.order_weekday ? (weekdays[row.order_weekday - 1] ?? "") : ""), {
        headerKey: "column.supplier.order-day",
        filter: "select",
        defaultVisible: false,
      }),
      c.text("subcontract_warehouse_id", (row) => (row.subcontract_warehouse_id ? (warehouseCode.get(row.subcontract_warehouse_id) ?? "") : ""), {
        headerKey: "column.supplier.subcontract-warehouse",
        filter: "select",
        defaultVisible: false,
      }),
      c.text("address_line", (row) => row.address_line ?? "", {
        headerKey: "column.address",
        defaultVisible: false,
      }),
      c.text("postal_code", (row) => row.postal_code ?? "", {
        headerKey: "column.postal-code",
        defaultVisible: false,
      }),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", defaultVisible: false }),
      c.custom(
        "is_active",
        (row) => (row.is_active ? "ACTIVE" : "INACTIVE"),
        (row) => (
          <Badge variant={row.is_active ? "success" : "error"} size="sm">
            {row.is_active ? t("status.active", "Active") : t("status.inactive", "Inactive")}
          </Badge>
        ),
        {
          headerKey: "column.status",
          filter: "select",
          filterOptions: [
            { value: "ACTIVE", label: t("status.active", "Active") },
            { value: "INACTIVE", label: t("status.inactive", "Inactive") },
          ],
          priority: 2,
        },
      ),
    ];
  }, [t, measuredBySupplier, weekdays, warehouseCode]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.suppliers", "Suppliers")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title={t("navigation.item.suppliers", "Suppliers")} />
        <div className="p-4 lg:p-6">
          <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t("error.generic", "Something went wrong. Please try again.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.suppliers", "Suppliers")}
        subtitle={t("suppliers.subtitle", "Who the plant buys from.")}
        badges={
          data ? (
            <Badge variant="neutral" size="sm">
              {t("suppliers.count", "{{active}} active of {{total}}", {
                active: data.filter((row) => row.is_active).length,
                total: data.length,
              })}
            </Badge>
          ) : null
        }
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="size-4" />
              {t("button.add-supplier", "Add supplier")}
            </Button>
          ) : null
        }
      />

      <DataTable
        data={data ?? []}
        columns={supplierCols}
        getRowId={(row) => String(row.id)}
        stateKey="/manufacturing/suppliers"
        loading={isLoading}
        initialSorting={[{ id: "order_count", desc: true }]}
        onRowClick={(row) =>
          void navigate({ to: "/manufacturing/suppliers/$supplierId", params: { supplierId: String(row.id) } })
        }
        emptyMessage={t("table.empty", "Nothing to show")}
      />
      <NewSupplierDialog open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

/**
 * A supplier needs a code, a name and a currency to exist; the rest is filled
 * on its page, where this lands.
 */
function NewSupplierDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const blank = (): SupplierDTO => ({
    id: 0,
    code: "",
    name: "",
    legal_name: null,
    address_line: null,
    city: null,
    postal_code: null,
    country: null,
    phone: null,
    fax: null,
    website: null,
    currency: "CAD",
    payment_terms: null,
    payment_terms_days: null,
    deposit_percent: null,
    lead_time_mode: "FIXED",
    lead_time_days: null,
    transit_days: null,
    order_cycle_days: null,
    order_weekday: null,
    subcontract_warehouse_id: null,
    note: null,
    is_active: true,
    created_date: null,
  });
  const [draft, setDraft] = useState<SupplierDTO>(blank);
  const [touched, setTouched] = useState(false);
  // Each opening starts blank: a code typed and then cancelled must not come back.
  useEffect(() => {
    if (open) {
      setDraft(blank());
      setTouched(false);
    }
  }, [open]);

  const errors = supplierErrors(draft, t);

  const create = useMutation({
    mutationFn: () => new SupplierClient(http).create({ ...draft, code: draft.code.trim(), name: draft.name.trim() }),
    onSuccess: async (supplier) => {
      await queryClient.invalidateQueries({ queryKey: ["supplier"] });
      onClose();
      toast.success(t("notification.saved", "Saved"));
      void navigate({
        to: "/manufacturing/suppliers/$supplierId",
        params: { supplierId: String(supplier.id) },
        search: { tab: "details" },
      });
    },
    // A code already taken comes back as a sentence.
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("button.add-supplier", "Add supplier")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={errors !== null}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            {t("supplier.create", "Create supplier")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("column.supplier.code", "Code")} htmlFor="supplier-new-code" required error={touched ? errors?.code : undefined}>
          <Input
            id="supplier-new-code"
            value={draft.code}
            autoFocus
            onChange={(event) => setDraft({ ...draft, code: event.target.value })}
            onBlur={() => setTouched(true)}
          />
        </Field>
        <Field label={t("column.name", "Name")} htmlFor="supplier-new-name" required error={touched ? errors?.name : undefined}>
          <Input id="supplier-new-name" value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
        </Field>
        <Field label={t("column.currency", "Currency")} htmlFor="supplier-new-currency" required>
          <Select
            id="supplier-new-currency"
            value={draft.currency}
            options={CURRENCIES.map((currency) => ({ value: currency, label: currency }))}
            onChange={(event) => setDraft({ ...draft, currency: event.target.value as SupplierDTO["currency"] })}
          />
        </Field>
        <p className="text-[12px] text-ink-muted">
          {t("supplier.new.hint", "Contacts, terms and the rest are filled on the supplier's page, which opens next.")}
        </p>
      </div>
    </Dialog>
  );
}

/**
 * The typed lead time, with what the receipts say beside it. Drift — measured
 * further from typed than the purchasing settings allow — is shown in amber so
 * a buyer notices it; the mode says which of the two planning uses.
 */
function LeadTimeCell({ supplier, measured }: { supplier: SupplierDTO; measured: SupplierLeadTimeReview | undefined }) {
  const { t } = useTranslation();
  const typed =
    supplier.lead_time_days === null ? (
      <span className="text-ink-faint">{t("supplier.default", "Default")}</span>
    ) : (
      <span>{t("supplier.days", "{{n}} d", { n: supplier.lead_time_days })}</span>
    );
  if (!measured?.measured_days) return <span className="tabular-nums">{typed}</span>;
  const onTime = measured.on_time_rate === null ? null : Math.round(measured.on_time_rate * 100);
  return (
    <span className="inline-flex items-baseline gap-2 tabular-nums">
      {typed}
      <span
        className={measured.is_drifting ? "text-[11px] font-medium text-warn" : "text-[11px] text-ink-muted"}
        title={t("supplier.measured.title", "Median from release to first receipt, over {{count}} receipts", { count: measured.receipt_count })}
      >
        {onTime === null
          ? t("supplier.measured", "measured {{days}} d ({{count}})", { days: measured.measured_days, count: measured.receipt_count })
          : t("supplier.measured-on-time", "measured {{days}} d ({{count}}), {{rate}}% on time", {
              days: measured.measured_days,
              count: measured.receipt_count,
              rate: onTime,
            })}
      </span>
    </span>
  );
}
